import { Component, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ClientSupportMessage } from 'src/app/shared/models/client-support-message.model';
import { BackendApiService } from 'src/app/shared/services/backend-api.service';
import { TurnstileService } from 'src/app/shared/services/turnstile.service';
import { TurnstileComponent } from 'src/app/shared/components/turnstile/turnstile.component';
import { ValidateNotEmptyInput } from 'src/app/shared/validators/is-not-empty.validator';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-contact-us',
  templateUrl: './contact-us.component.html',
  styleUrls: ['./contact-us.component.scss']
})
export class ContactUsComponent implements OnInit, AfterViewInit {
  @ViewChild('turnstileWidget') turnstileWidget!: TurnstileComponent;

  contactForm!: FormGroup;
  isSubmitting = false;
  showSuccess = false;
  showError = false;
  errorMessage = '';
  turnstileToken: string | null = null;
  siteKey = environment.turnstileSiteKey;

  constructor(
    private formBuilder: FormBuilder,
    private backendApiService: BackendApiService,
    private turnstileService: TurnstileService,
  ) { }

  ngOnInit(): void {
    this.contactForm = this.formBuilder.group({
      name: ['', [Validators.required, ValidateNotEmptyInput, Validators.maxLength(100)]],
      email: ['', [
        Validators.required,
        ValidateNotEmptyInput,
        Validators.email,
        Validators.maxLength(100)
      ]],
      message: ['', [
        Validators.required,
        ValidateNotEmptyInput,
        Validators.minLength(10),
        Validators.maxLength(1000)
      ]]
    });
  }

  ngAfterViewInit(): void {
    this.initializeAccordion();

    // Scroll down after 1 seconds
    setTimeout(() => {
      window.scrollBy({
        top: 80,
        behavior: 'smooth'
      });
    }, 1000);
  }



  private initializeAccordion(): void {
    setTimeout(() => {
      const accordionButtons = document.querySelectorAll('.accordion-button');
      accordionButtons.forEach(button => {
        button.addEventListener('click', () => {
          const accordionItem = button.parentElement;
          if (accordionItem) {
            accordionItem.classList.toggle('active');

            // Close other accordion items
            const siblings = Array.from(accordionItem.parentElement?.children || [])
              .filter(el => el !== accordionItem);

            siblings.forEach(sibling => {
              sibling.classList.remove('active');
            });
          }
        });
      });
    }, 0);
  }

  hasError(controlName: string, errorName: string) {
    const control = this.contactForm.controls[controlName];
    return control.touched && control.hasError(errorName);
  }

  onTurnstileToken(token: string | null): void {
    this.turnstileToken = token;
    this.turnstileService.setToken(token);
  }

  submitForm() {
    if (this.contactForm.valid && this.turnstileToken) {
      this.isSubmitting = true;
      this.showError = false;
      this.showSuccess = false;

      const message: ClientSupportMessage = {
        name: this.contactForm.get('name')?.value,
        email: this.contactForm.get('email')?.value,
        message: this.contactForm.get('message')?.value
      };

      this.backendApiService.sendClientSupportMessage(message).subscribe({
        next: () => {
          this.showSuccess = true;
          this.contactForm.reset();
          this.isSubmitting = false;
          this.turnstileWidget?.reset();
          this.turnstileService.clearToken();
          this.turnstileToken = null;
        },
        error: (err) => {
          this.showError = true;
          if (err.error && err.error.messages && err.error.messages.length > 0) {
            this.errorMessage = err.error.messages
              .map((msg: any) => msg.content)
              .filter((content: string | undefined) => content)
              .join(', ');
          } else if (err.error && typeof err.error === 'string') {
            this.errorMessage = err.error;
          } else {
            this.errorMessage = 'An error occurred while sending your message. Please try again later.';
          }
          this.isSubmitting = false;

          if (err.status === 400 || err.status === 403) {
            this.turnstileWidget?.reset();
            this.turnstileService.clearToken();
            this.turnstileToken = null;
          }
        }
      });
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.contactForm.controls).forEach(key => {
        const control = this.contactForm.get(key);
        control?.markAsTouched();
      });
    }
  }
}
